import { useNavigate } from "react-router-dom";
import { useCachedGet } from "@/lib/cache";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, Legend } from "recharts";
import { Package, Layers, CheckCircle2, TrendingUp, Gauge, AlertTriangle, Users } from "lucide-react";

const FASE_COLOR = {
  "Desarrollo · Horma": "#a855f7",
  "Diseño": "#8b5cf6",
  "Prototipo": "#3b82f6",
  "Muestra": "#f59e0b",
  "Cierre": "#0ea5e9",
  "Alta de Producto": "#ec4899",
  "Liberado": "#14b8a6",
  "Depurado": "#64748b",
};

function Metric({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card className="p-5 border-slate-200 shadow-sm" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-slate-500">{label}</span>
        <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${tint}`}>
          <Icon size={18} />
        </div>
      </div>
      <div className="mt-3 font-head text-3xl font-extrabold tracking-tight">{value}</div>
    </Card>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data } = useCachedGet("/dashboard");

  return (
    <div className="p-6 lg:p-8 max-w-7xl">
      <div className="mb-8">
        <p className="text-sm text-slate-500">Hola, {user?.nombre} 👋</p>
        <h1 className="font-head text-3xl font-extrabold tracking-tight mt-0.5">Dashboard</h1>
        <p className="text-slate-500 mt-1">Visibilidad en tiempo real del avance de todos los modelos.</p>
      </div>

      {!data ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28 rounded-xl" />)}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Metric icon={Package} label="Modelos totales" value={data.total_modelos} tint="bg-blue-50 text-blue-600" testid="metric-modelos" />
            <Metric icon={Layers} label="Colecciones" value={data.total_colecciones} tint="bg-violet-50 text-violet-600" testid="metric-colecciones" />
            <Metric icon={CheckCircle2} label="Completos (liberados + depurados)" value={data.modelos_completos} tint="bg-emerald-50 text-emerald-600" testid="metric-completos" />
            <Metric icon={TrendingUp} label="Avance promedio" value={`${data.avance_promedio}%`} tint="bg-amber-50 text-amber-600" testid="metric-avance" />
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <Card className="p-6 border-slate-200 shadow-sm">
              <h2 className="font-head text-lg font-bold tracking-tight">Avance por colección</h2>
              <div className="mt-5 space-y-5">
                {data.avance_colecciones.map((c) => (
                  <div key={c.coleccion_id} data-testid={`avance-col-${c.coleccion_id}`}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="font-medium text-slate-700">{c.nombre} <span className="text-slate-400">· {c.modelos} modelos</span></span>
                      <span className="font-semibold tabular-nums">{c.avance}%</span>
                    </div>
                    <Progress value={c.avance} className="h-2" />
                  </div>
                ))}
                {data.avance_colecciones.length === 0 && <p className="text-sm text-slate-400">Sin colecciones aún.</p>}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 shadow-sm">
              <h2 className="font-head text-lg font-bold tracking-tight text-center">Modelos por etapa actual</h2>
              <p className="text-xs text-slate-400 mt-0.5 text-center">Cantidad de modelos según su próxima compuerta pendiente.</p>
              <div className="mt-4 h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={data.distribucion_fase} margin={{ top: 10, right: 10, left: 10, bottom: 34 }}>
                    <XAxis dataKey="fase" interval={0} height={56} tickMargin={8} angle={-28} textAnchor="end" tickFormatter={(v) => v === "Desarrollo · Horma" ? "Horma" : v === "Alta de Producto" ? "Por liberar" : v} tick={{ fontSize: 10.5, fill: "#64748b" }} axisLine={false} tickLine={false} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                    <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                    <Bar dataKey="cantidad" radius={[6, 6, 0, 0]}>
                      {data.distribucion_fase.map((e) => (
                        <Cell key={e.fase} fill={FASE_COLOR[e.fase] || "#94a3b8"} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap justify-center gap-3 mt-2" data-testid="dist-fase-legend">
                {Object.entries(FASE_COLOR).map(([f, color]) => (
                  <span key={f} data-testid={`legend-${f}`} className="flex items-center gap-1.5 text-xs text-slate-500">
                    <span className="h-2.5 w-2.5 rounded-full" style={{ background: color }} /> {f === "Desarrollo · Horma" ? "Horma" : f === "Alta de Producto" ? "Alta de Producto · Por liberar" : f}
                  </span>
                ))}
              </div>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-6 mt-6">
            <Card className="p-6 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <Gauge size={17} className="text-slate-400" />
                <h2 className="font-head text-lg font-bold tracking-tight">Cuellos de botella por paso</h2>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Dónde se acumulan los modelos y cuántos días hábiles llevan detenidos en promedio.</p>
              <div className="mt-4 space-y-3">
                {(data.cuellos_botella || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">No hay modelos en proceso.</p>
                ) : (
                  data.cuellos_botella.map((c) => (
                    <div key={c.numero} className="flex items-center gap-3" data-testid={`cuello-${c.numero}`}>
                      <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: FASE_COLOR[c.fase] || "#94a3b8" }} />
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-700 truncate">{c.nombre} <span className="text-slate-400 font-normal">({c.fase})</span></div>
                        <div className="text-[11px] text-slate-400">{c.fase}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <span className="text-sm font-semibold tabular-nums text-slate-800">{c.modelos}</span>
                        <span className="text-[11px] text-slate-400"> modelo{c.modelos !== 1 ? "s" : ""}</span>
                      </div>
                      <div className="w-16 text-right shrink-0">
                        <span className={`text-xs font-semibold tabular-nums ${c.dias_promedio >= (data.umbral_atorado || 3) ? "text-amber-600" : "text-slate-500"}`}>
                          {c.dias_promedio} d
                        </span>
                        <div className="text-[10px] text-slate-400">prom.</div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>

            <Card className="p-6 border-slate-200 shadow-sm">
              <div className="flex items-center gap-2">
                <AlertTriangle size={17} className="text-amber-500" />
                <h2 className="font-head text-lg font-bold tracking-tight">Modelos más atorados</h2>
              </div>
              <p className="text-xs text-slate-400 mt-0.5">Modelos con {data.umbral_atorado || 3}+ días hábiles esperando en su paso actual. Da clic para abrir la ficha.</p>
              <div className="mt-4 space-y-2">
                {(data.modelos_atorados || []).length === 0 ? (
                  <p className="text-sm text-slate-400 py-4">Ningún modelo rebasa el umbral. 🎉</p>
                ) : (
                  data.modelos_atorados.map((m) => (
                    <button
                      key={m.modelo_id}
                      onClick={() => navigate(`/modelos/${m.modelo_id}`)}
                      data-testid={`atorado-${m.modelo_id}`}
                      className="w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-slate-100"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-slate-800 truncate">{m.nombre} <span className="text-slate-400 font-mono text-xs">{m.sku}</span></div>
                        <div className="text-[11px] text-slate-400">Detenido en {m.paso_orden ?? m.paso_numero}. {m.paso_nombre}</div>
                      </div>
                      <span className="shrink-0 text-xs font-bold px-2 py-1 rounded-full bg-amber-50 text-amber-700 tabular-nums">{m.dias} {m.dias === 1 ? "día hábil" : "días hábiles"}</span>
                    </button>
                  ))
                )}
              </div>
            </Card>
          </div>

          <Card className="p-6 border-slate-200 shadow-sm mt-6" data-testid="modelos-por-disenador">
            <div className="flex items-center gap-2">
              <Users size={17} className="text-slate-400" />
              <h2 className="font-head text-lg font-bold tracking-tight">Modelos por diseñador</h2>
            </div>
            <p className="text-xs text-slate-400 mt-0.5">Cuántos modelos tiene asignados cada diseñador, por estado.</p>
            <div className="mt-4" style={{ height: Math.max(160, (data.modelos_por_disenador || []).length * 46 + 40) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={data.modelos_por_disenador || []} margin={{ top: 4, right: 24, left: 8, bottom: 4 }}>
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 11, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="nombre" width={130} tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                  <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 10, border: "1px solid #e2e8f0", fontSize: 12 }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="en_proceso" name="En proceso" stackId="d" fill="#3b82f6" />
                  <Bar dataKey="liberados" name="Liberados" stackId="d" fill="#14b8a6" />
                  <Bar dataKey="depurados" name="Depurados" stackId="d" fill="#94a3b8" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </Card>
        </>
      )}
    </div>
  );
}
